import React, { useState, useCallback } from 'react';
import { AccordionContent, AccordionItem } from '../../ui/accordion';
import { cn } from '../../../lib/utils';
import { KYPAccordionTrigger } from './KYPAccordionTrigger';
import { SectionSummary } from './SectionSummary';
import { ArticleReferencesList } from './ArticleReferencesList';
import { OrderedArticleReferencesList } from './OrderedArticleReferencesList';
import { SectionExplanation } from './SectionExplanation';
import { AgentDetailsSection } from './AgentDetailsSection';
import { SectionRating } from './SectionRating';
import { PlayerData } from '../../../types/player';

interface AnalysisSectionProps {
  id: string;
  title: string;
  data: any;
  hasData: boolean;
}

export const AnalysisSection: React.FC<AnalysisSectionProps> = ({ 
  id, 
  title, 
  data, 
  hasData 
}) => {
  const [articleIds, setArticleIds] = useState<string[]>([]);
  
  const handleArticleReferences = useCallback((ids: string[]) => {
    setArticleIds(ids);
  }, []);
  if (!hasData) {
    return (
      <AccordionItem 
        value={id}
        className="border-b border-gray-500 opacity-50"
      >
        <KYPAccordionTrigger 
          className="px-4"
          disabled={true}
          hasData={false}
        >
          <span>{title}</span>
        </KYPAccordionTrigger>
      </AccordionItem>
    );
  }

  // Extract rating score if available
  const getRatingScore = () => {
    if (data?.summary?.rating?.score) {
      return data.summary.rating.score;
    }
    if (data?.rating?.score) {
      return data.rating.score;
    }
    // Special handling for career section which might have a different structure
    if (id === 'career' && data?.rating) {
      console.log('Special handling for career rating:', data.rating);
      if (typeof data.rating === 'object' && data.rating !== null) {
        if ('score' in data.rating) {
          return data.rating.score;
        }
      }
    }
    return null;
  };

  const score = getRatingScore();

  return (
    <AccordionItem 
      value={id}
      className="border-b border-gray-500"
    >
      <KYPAccordionTrigger 
        className="px-4"
        hasData={true}
      >
        <div className="flex items-center gap-2">
          <span>{title}</span>
          {score && <SectionRating score={score} />}
        </div>
      </KYPAccordionTrigger>
      <AccordionContent className="px-4">
        <div className="rounded-lg bg-[#1A1A1A] p-6 space-y-6 border border-gray-500">
          {/* Summary Section */}
          <SectionSummary 
            summary={data.summary} 
            onArticleReferences={handleArticleReferences}
          />
          
          {/* Article References Section */}
          {data.ordered_article_summaries && data.ordered_article_summaries.length > 0 ? (
            <OrderedArticleReferencesList articles={data.ordered_article_summaries} />
          ) : articleIds.length > 0 ? (
            <OrderedArticleReferencesList articles={[]} />
          ) : data.article_references ? (
            <ArticleReferencesList references={data.article_references} />
          ) : null}
          
          {/* Rating Explanation Section */}
          {data.summary?.rating?.explanation && (
            <SectionExplanation 
              score={score} 
              explanation={data.summary.rating.explanation} 
            />
          )}
          
          {/* Agent Details Section - Only for agent section */}
          {id === 'agent' && <AgentDetailsSection />}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};